import React, { useMemo } from 'react';

import { ResourcedEmoji } from '@atlaskit/emoji/element';
import type { EmojiProvider } from '@atlaskit/emoji/types';
import FolderClosedIcon from '@atlaskit/icon/core/folder-closed';
import { token } from '@atlaskit/tokens';
import type { EmptyChatLaunchAction } from '@atlassian/conversation-assistant-store/controllers/ui-store';
import { ThreadCard } from '@atlassian/rovo-platform-ui-components/thread-card';
import { useSpaceStarters } from '@atlassian/rovo-spaces/common/starters';
import type { Space, SpaceSource } from '@atlassian/rovo-spaces/controllers/space-store/types';

import { SourcesPopupTrigger } from '../../sources/SourcesPopupTrigger';

type SourceType =
	| 'ConfluencePage'
	| 'JiraIssue'
	| 'BitbucketRepository'
	| 'LoomVideo'
	| 'TownsquareLink';

const SOURCE_TYPE_MAP: Record<string, SourceType> = {
	ConvoAiHomeThreadsConfluenceSourceData: 'ConfluencePage',
	ConvoAiHomeThreadsJiraSourceData: 'JiraIssue',
	ConvoAiHomeThreadsBitbucketSourceData: 'BitbucketRepository',
	ConvoAiHomeThreadsLoomSourceData: 'LoomVideo',
};

export interface SpaceCardProps {
	space: Space;
	sources: SpaceSource[];
	emojiProvider: Promise<EmojiProvider> | null;
	onChatClick: (
		spaceId: string,
		spaceName: string,
		spaceEmoji: string,
		customConversationActions?: EmptyChatLaunchAction[],
	) => void;
	onDailyBriefClick: (spaceId: string, spaceName: string, spaceEmoji: string) => void;
}

/** Renders a single space as a ThreadCard with memoized empty chat actions. */
export const SpaceCard = ({
	space,
	sources,
	emojiProvider,
	onChatClick,
	onDailyBriefClick,
}: SpaceCardProps): React.JSX.Element => {
	const emptyChatActions = useSpaceStarters({
		spaceId: space.id,
		spaceName: space.name,
	});

	const mappedSources = useMemo(() => {
		if (!sources.length) {
			return [];
		}
		return sources.map((source) => ({
			ari: source.ari ?? '',
			title: source.name ?? source.url ?? '',
			type: (SOURCE_TYPE_MAP[source.sourceType ?? ''] ?? 'TownsquareLink') as SourceType,
			url: source.url ?? '',
			lastModified: '',
		}));
	}, [sources]);

	return (
		<ThreadCard
			threadId={space.id}
			title={space.name}
			renderIcon={
				space.emoji && emojiProvider ? (
					<ResourcedEmoji
						emojiId={{ id: space.emoji, shortName: '' }}
						emojiProvider={emojiProvider}
						fitToHeight={16}
						showTooltip={false}
					/>
				) : (
					<FolderClosedIcon label="" color={token('color.text.subtlest')} />
				)
			}
			renderSources={
				mappedSources.length > 0 ? (
					<SourcesPopupTrigger sources={mappedSources} testId="rovo-for-you-space-sources-button" />
				) : undefined
			}
			onDailyBriefClick={(id, name) => onDailyBriefClick(id, name, space.emoji ?? '')}
			onChatClick={(id, name) => onChatClick(id, name, space.emoji ?? '', emptyChatActions)}
		/>
	);
};
